/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { LabelBlueBold } from "../../../Layout/Shared/LabelBlueBold";
import { ClientMultiSelect } from "../ClientMultiSelect";
import moment from "moment";
import {
  getOnlyDate,
  getOnlyTime,
  getTodayDateString,
  getAvailableTimesForDate
} from "../../../../utils/date";
import ErrorField from "../../../../components/Layout/Shared/ErrorField";
import { BusinessCreateAppointmentSchema } from "../../../../schemas/Calendar/Business/appointment";
import { RightToggler } from "../../../../components/Layout/Shared/RightToggler";
import { createAppointment } from "../../../../data/Calendar/Business/createAppointment";
import { list as getClients } from "../../../../data/Client/Business/get-list";

export const CreateAppointment = ({ date, close, onSuccess }) => {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  const getInitialTime = (dateValue) => {
    const dateStr = getOnlyDate(dateValue);
    const availableTimes = getAvailableTimesForDate(dateStr);
    const requestedTime = getOnlyTime(dateValue);
    return availableTimes.includes(requestedTime) ? requestedTime : availableTimes[0];
  };

  const [appointment, setAppointment] = useState({
    invitees: [],
    appointmentDate: getOnlyDate(date),
    appointmentTime: getInitialTime(date),
    reminder: "15",
    pushNotification: true,
    notes: ""
  });

  useEffect(() => {
    const fetchClients = async () => {
      const { hasSuccess, data } = await getClients();
      if (hasSuccess && Array.isArray(data)) {
        setClients(data);
      }
      setClientsLoading(false);
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!date) return;
    const newDate = getOnlyDate(date);
    const availableTimes = getAvailableTimesForDate(newDate);
    const currentTime = appointment.appointmentTime;
    const validTime = availableTimes.includes(currentTime) ? currentTime : availableTimes[0];
    setAppointment({
      ...appointment,
      appointmentDate: newDate,
      appointmentTime: validTime
    });
    return () => {};
  }, [date]);

  const onSubmit = async values => {
    const {
      invitees,
      appointmentDate, appointmentTime, reminder, pushNotification, notes
    } = values;
    const _date = moment(
      `${appointmentDate} ${appointmentTime}`,
      "YYYY-MM-DD HH:mm A"
    ).format();
    const { hasSuccess } = await createAppointment({
      invitees,
      date: _date, reminder, pushNotification, notes,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      source: "app"
    });
    hasSuccess && onSuccess();
    close();
  };

  return (
    <RightToggler title="CREATE APPOINTMENT" size="500px" close={close}>
      <Formik
        validationSchema={BusinessCreateAppointmentSchema}
        validateOnBlur={false}
        initialValues={appointment}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ errors, setFieldValue, values, validateForm, isSubmitting }) => (
          <Form className="p-3">
            <LabelBlueBold content="Select Clients" />
            <ClientMultiSelect
              clients={clients}
              selectedClients={values.invitees}
              onChange={(selected) => setFieldValue("invitees", selected)}
              loading={clientsLoading}
              placeholder="Search clients to invite..."
            />
            <ErrorField message={typeof errors.invitees === 'string' ? errors.invitees : ''} />

            <hr className="my-4" />

            <div className="row">
              <div className="col-12 col-md-6">
                <LabelBlueBold content="Apt. Date" />
                <Field name="appointmentDate">
                  {({ field: { value }, form: { setFieldValue, values } }) => (
                    <input
                      type="date"
                      className="custom-input bg-transparent"
                      value={value}
                      min={getTodayDateString()}
                      onChange={({ target }) => {
                        const newDate = target.value;
                        setFieldValue("appointmentDate", newDate, false);
                        const availableTimes = getAvailableTimesForDate(newDate);
                        if (!availableTimes.includes(values.appointmentTime)) {
                          setFieldValue("appointmentTime", availableTimes[0], true);
                        } else {
                          setFieldValue("appointmentTime", values.appointmentTime, true);
                        }
                      }}
                    />
                  )}
                </Field>
                <ErrorField message={errors.appointmentDate} />
              </div>
              <div className="col-12 col-md-6 mt-3 mt-md-0">
                <LabelBlueBold content="Apt. Time" />
                <Field name="appointmentTime">
                  {({ field: { value }, form: { setFieldValue, values } }) => (
                    <select
                      className="primary-select bg-transparent"
                      value={value}
                      onChange={({ target }) => setFieldValue("appointmentTime", target.value, true)}
                    >
                      {getAvailableTimesForDate(values.appointmentDate).map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  )}
                </Field>
                <ErrorField message={errors.appointmentTime} />
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12 col-md-6">
                <LabelBlueBold content="Push Notification" />
                <Field name="pushNotification">
                  {({ field: { value }, form: { setFieldValue } }) => (
                    <select
                      className="primary-select bg-transparent"
                      value={value}
                      onChange={({ target }) => setFieldValue("pushNotification", target.value === "true")}
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  )}
                </Field>
              </div>
              <div className="col-12 col-md-6 mt-3 mt-md-0">
                <LabelBlueBold content="Reminder Time" />
                <Field name="reminder">
                  {({ field: { value }, form: { setFieldValue, values } }) => (
                    <select
                      className="primary-select bg-transparent"
                      value={value}
                      disabled={!values.pushNotification}
                      onChange={({ target }) => setFieldValue("reminder", target.value)}
                    >
                      <option value={15}>15 Minutes Earlier Apt.</option>
                      <option value={30}>30 Minutes Earlier Apt.</option>
                      <option value={60}>60 Minutes Earlier Apt.</option>
                    </select>
                  )}
                </Field>
              </div>
            </div>

            <hr className="my-4" />

            <LabelBlueBold content="Notes" />
            <Field
              as="textarea"
              name="notes"
              className="custom-input bg-transparent"
              placeholder="Add notes..."
              style={{ minHeight: "120px", resize: "vertical" }}
            />

            <button
              type="submit"
              className="btn-blue btn-small w-100 mt-4 mb-3"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Scheduling..." : "Schedule Appointment"}
            </button>
          </Form>
        )}
      </Formik>
    </RightToggler>
  );
};
